import React, { useEffect, useState } from 'react';
import { motion, useSpring, useTransform } from 'framer-motion';
import { CalculatorIcon, SlidersHorizontalIcon } from 'lucide-react';
import { InteriorSlide } from './SlideShell';
import type { SlideProps } from './SlideShell';
type LifecyclePhase = 'launch' | 'growth' | 'established';
interface PhaseConfig {
  label: string;
  portfolioLabel: string;
  sliderLabel: string;
  share: number;
  min: number;
  max: number;
  rationale: string;
}
const PHASES: Record<LifecyclePhase, PhaseConfig> = {
  launch: {
    label: 'Launch',
    portfolioLabel: 'Launch Brands',
    sliderLabel: '% of revenue from Launch Brands',
    share: 8,
    min: 1,
    max: 25,
    rationale: 'For Launch Brands, the model illustrates the revenue value of improving early adoption across the selected share of group revenue.'
  },
  growth: {
    label: 'Growth',
    portfolioLabel: 'Growth Brands',
    sliderLabel: '% of revenue from Growth Brands',
    share: 25,
    min: 5,
    max: 60,
    rationale: 'For Growth Brands, the model illustrates the revenue value of increasing adoption, frequency, or share across the selected portfolio.'
  },
  established: {
    label: 'Established',
    portfolioLabel: 'Established Brands',
    sliderLabel: '% of revenue from Established Brands',
    share: 13.5,
    min: 5,
    max: 40,
    rationale: 'For Established Brands, the model illustrates how a small improvement can unlock meaningful value from an established revenue base, even as resources shift toward newer products.'
  }
};
export function SlideValueCalculator({}: SlideProps) {
  const [phase, setPhase] = useState<LifecyclePhase>('growth');
  const [revenueB, setRevenueB] = useState(32);
  const [phasePercent, setPhasePercent] = useState(0);
  const [improvementPercent, setImprovementPercent] = useState(0.5);
  const [animationDone, setAnimationDone] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const phaseConfig = PHASES[phase];
  useEffect(() => {
    const timers = [setTimeout(() => setPhasePercent(PHASES.growth.share), 1000), setTimeout(() => setImprovementPercent(1), 1800), setTimeout(() => setAnimationDone(true), 2800)];
    return () => timers.forEach(clearTimeout);
  }, []);
  const upside = revenueB * 1000 * (phasePercent / 100) * (improvementPercent / 100);
  const upsideValue = useSpring(0, {
    bounce: 0,
    duration: 1600
  });
  const displayUpside = useTransform(upsideValue, latest => Math.round(latest).toLocaleString());
  useEffect(() => {
    if (revealed) upsideValue.set(upside);
  }, [revealed, upside, upsideValue]);
  const handleChange = (setter: (value: number) => void) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setter(Number(event.target.value));
  };
  const selectPhase = (nextPhase: LifecyclePhase) => {
    setPhase(nextPhase);
    setPhasePercent(PHASES[nextPhase].share);
  };
  const sliders = [{
    label: 'Group Revenue',
    value: `€${revenueB.toFixed(1)}bn`,
    valueClass: 'text-[var(--fg)]',
    min: 5,
    max: 65,
    step: 0.5,
    current: revenueB,
    onChange: handleChange(setRevenueB),
    footer: ['€5bn', 'Illustrative: €32bn', '€65bn']
  }, {
    label: phaseConfig.sliderLabel,
    value: `€${(revenueB * phasePercent / 100).toFixed(1)}bn`,
    valueClass: 'text-[var(--fg)]',
    min: phaseConfig.min,
    max: phaseConfig.max,
    step: 0.5,
    current: phasePercent,
    onChange: handleChange(setPhasePercent),
    footer: [`${phaseConfig.min}%`, `${phasePercent.toFixed(1)}% — illustrative`, `${phaseConfig.max}%`]
  }, {
    label: 'Target Improvement',
    value: `${improvementPercent.toFixed(1)}%`,
    valueClass: 'text-[var(--brand)]',
    min: 0.5,
    max: 5,
    step: 0.5,
    current: improvementPercent,
    onChange: handleChange(setImprovementPercent),
    footer: ['0.5%', '', '5%']
  }];
  return <InteriorSlide data-id="element-481" data-mp="gg7esj9klq2d">
      <motion.header initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.6
    }} className="mb-[clamp(1rem,1.8vw,1.6rem)] flex-shrink-0" data-id="element-482" data-mp="ggpks7m1binh">
        <div className="mb-3 flex items-center gap-3" data-id="element-483" data-mp="qdc06gvldhi4">
          <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-484" data-mp="verk25m19hyx">
            Illustrative model
          </span>
          <span className="h-px w-12 bg-black/10" data-id="element-485" data-mp="3tu0zpiwrbqya" />
          <span className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]" data-id="element-486" data-mp="2p13lpwxi3zn">
            Across the full lifecycle
          </span>
        </div>

        <motion.h2 initial={{
        opacity: 0,
        y: 5
      }} animate={{
        opacity: 1,
        y: 0
      }} className="max-w-5xl text-[clamp(1.6rem,3vw,2.75rem)] font-bold leading-[1.1] tracking-tight text-[var(--fg)]" data-id="element-487" data-mp="7n2rqp97othz">
          Even a{' '}
          <span className="text-[var(--brand)]" data-id="element-488" data-mp="3g3fn2bxwcd8u">
            {improvementPercent.toFixed(1)}%
          </span>{' '}
          improvement across the lifecycle equals
        </motion.h2>
      </motion.header>

      <div className="grid min-h-0 flex-1 grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-14" data-id="element-489" data-mp="1t8iutzu6lz8y">
        <div className="relative space-y-5 rounded-3xl border border-black/5 bg-[var(--surface)] p-[clamp(1.25rem,2vw,2rem)] lg:col-span-5" data-id="element-490" data-mp="1o7d0xyosc5xu">
          <div data-id="element-491" data-mp="1eaqj7irr9v01">
            <div className="mb-2 flex items-center gap-2 text-[var(--brand)]" data-id="element-492" data-mp="1eba1j0lbilel">
              <SlidersHorizontalIcon className="h-4 w-4" aria-hidden="true" data-id="element-493" data-mp="19k1qk04carzb" />
              <p className="text-[10px] font-bold uppercase tracking-[0.15em]" data-id="element-494" data-mp="19hxc4o8teaks">
                Select lifecycle phase
              </p>
            </div>
            <div className="grid grid-cols-3 rounded-xl border border-[var(--brand)]/15 bg-white p-1 shadow-sm" role="group" aria-label="Select lifecycle phase" data-id="element-495" data-mp="10kemt7o51a33">
              {(Object.keys(PHASES) as LifecyclePhase[]).map(phaseKey => {
              const active = phase === phaseKey;
              return <button key={phaseKey} type="button" onClick={() => selectPhase(phaseKey)} aria-pressed={active} className={`rounded-lg px-2 py-2 text-xs font-bold transition ${active ? 'bg-[var(--brand)] text-white shadow-sm' : 'text-[var(--muted)] hover:bg-[var(--surface)] hover:text-[var(--fg)]'}`} data-id="element-496" data-mp="lwd9xkaq95fu">
                    {PHASES[phaseKey].label}
                  </button>;
            })}
            </div>
          </div>

          {sliders.map(slider => <div key={slider.label} className="space-y-2.5" data-id="element-497" data-mp="1in5pmgtr0vh3">
              <div className="flex items-end justify-between gap-4" data-id="element-498" data-mp="1ilm7ekoj8aco">
                <label htmlFor={slider.label} className="text-sm font-semibold text-[var(--fg)]" data-id="element-499" data-mp="1nenpf1k1cq0h">
                  {slider.label}
                </label>
                <span className={`text-2xl font-bold ${slider.valueClass}`} data-id="element-500" data-mp="3346hc32wcbl4">
                  {slider.value}
                </span>
              </div>
              <input id={slider.label} type="range" min={slider.min} max={slider.max} step={slider.step} value={slider.current} onChange={slider.onChange} className="deck-slider w-full" data-id="element-501" data-mp="3curbnebjety8" />
              <div className="flex justify-between text-xs text-[var(--muted)]" data-id="element-502" data-mp="2z3mk6zibp7yz">
                <span data-id="element-503" data-mp="2u3qhd3ld3ml0">{slider.footer[0]}</span>
                <span className="text-[var(--brand)]" data-id="element-504" data-mp="2u2ow66amtjpk">{slider.footer[1]}</span>
                <span data-id="element-505" data-mp="ec2iv3qp9m03">{slider.footer[2]}</span>
              </div>
            </div>)}
        </div>

        <div className="flex flex-col justify-center lg:col-span-7" data-id="element-506" data-mp="9d9a1omcyj69">
          {revealed ? <motion.div key={`revealed-${phase}`} initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.35,
          ease: [0.23, 1, 0.32, 1]
        }} className="space-y-4" data-id="element-507" data-mp="22y2ong9b7jk6">
              <h3 className="text-base font-semibold uppercase tracking-[0.2em] text-[var(--muted)]" data-id="element-508" data-mp="2hmpfae4dzxqp">
                Unlocked Value
              </h3>
              <div className="flex items-baseline gap-2 text-[var(--brand)]" data-id="element-509" data-mp="2mn3bmywmlj8w">
                <span className="text-[clamp(1.75rem,3vw,3rem)] font-bold" data-id="element-510" data-mp="2qkj1gw8ftb05">€</span>
                <motion.span className="text-[clamp(3.5rem,8vw,7rem)] font-bold leading-none tracking-tight" data-id="element-511" data-mp="2vlykyjuyv9gf">
                  {displayUpside}
                </motion.span>
                <span className="text-[clamp(1.75rem,3vw,3rem)] font-bold" data-id="element-512" data-mp="1187grzvephsd">m</span>
              </div>
              <p className="max-w-xl border-l-2 border-[var(--brand)]/40 py-1 pl-4 text-[clamp(0.95rem,1.2vw,1.1rem)] leading-relaxed text-[var(--muted)]" data-id="element-513" data-mp="165ph51vcyoor">
                {phaseConfig.rationale}
              </p>
            </motion.div> : <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.35,
          ease: [0.23, 1, 0.32, 1]
        }} className="max-w-xl space-y-6" data-id="element-514" data-mp="tueubjdy6t83">
              <h3 className="text-base font-semibold uppercase tracking-[0.2em] text-[var(--muted)]" data-id="element-515" data-mp="1hkspvjvqra7f">
                Unlocked Value
              </h3>
              <motion.p key={`rationale-${phase}`} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className="text-[clamp(0.95rem,1.2vw,1.1rem)] leading-relaxed text-[var(--muted)]" data-id="element-516" data-mp="1ckwn1nys5otg">
                {phaseConfig.rationale}
              </motion.p>
              <motion.button type="button" onClick={() => setRevealed(true)} whileHover={{
            scale: 1.03
          }} whileTap={{
            scale: 0.98
          }} animate={{
            opacity: animationDone ? 1 : 0.4
          }} className="inline-flex items-center gap-2 rounded-full bg-[var(--brand)] px-8 py-4 text-lg font-bold text-white shadow-lg shadow-[var(--brand)]/25 transition-colors hover:bg-[var(--brand-light)]" data-id="element-517" data-mp="3neak4vh4jskb">
                <CalculatorIcon className="h-5 w-5" aria-hidden="true" data-id="element-518" data-mp="3eqb0pr3ue2ud" />
                Calculate potential
              </motion.button>
            </motion.div>}
        </div>
      </div>
    </InteriorSlide>;
}